import { useCallback, useState } from 'react'
import type { JSX } from 'react'

import {
  JOB_TRANSITIONS,
  checkJobTransition,
  checkOrderTransition,
  dioptre,
  formatDioptre,
  resumeJob,
  type JobStatus,
  type OrderStatus,
  type QualityCheck,
  REMAKE_CAUSES,
} from '@lensmira/domain'

import { useWriteSettled } from '../queue/use-settled-write.js'
import { formatDay } from '@lensmira/i18n'

import { Ltr, useTranslation, type Translation } from '../i18n.js'
import { sentence } from '../sentence.js'
import { jobStatusLabel, remakeCauseLabel, remakeStatusWord } from '../status-labels.js'
import type { MessageKey } from '@lensmira/i18n'
import { connectionLine, type Translate } from '../queue/status-line.js'
import { useWriteQueue } from '../queue/use-write-queue.js'
import type { QueueSnapshot } from '../queue/types.js'

/**
 * Job detail and quality check. §6.8 `JOB-002`, `JOB-005a`; Appendix C.2, C.3.
 *
 * Three things here are the requirement and the rest is display.
 *
 * **Only the transitions Appendix C.2 permits from the job's current status.** `JOB_TRANSITIONS`
 * and `checkJobTransition` are `@lensmira/domain`'s, imported rather than re-derived: `jobs.service.ts`
 * says why, in its own words, "two implementations of one state machine diverge, and the one that
 * diverges silently is the one nobody is testing." The same risk exists on this side of the wire.
 *
 * **`ready` is unreachable except from a passed check, structurally.** `checkJobTransition` already
 * refuses it server-side; this screen asks the same function the same question before it ever
 * renders the button, so a job whose latest check failed or was never recorded has no "mark ready"
 * to press in the first place, not one that is pressed and then refused.
 *
 * **`collected` and `remake_raised` are legal edges in the table that this screen does not offer
 * as generic transitions.** Both need more than a status flip: `collected` posts revenue and COGS
 * (`ACC-002`) and takes a cash amount, so it goes through the dedicated collect action below, which
 * calls `POST /jobs/collect`. `remake_raised` needs a cost attribution and a cause (Appendix C.3),
 * which belongs to a remakes screen this task does not build. Offering either as a bare "move to X"
 * button here would flip the status with none of that machinery behind it.
 *
 * **The screen does not fetch.** `OFF-001a` is about writes, and reads are a later slice (`OFF-001b`),
 * but `apps/counter/eslint.config.js` narrows the one exception (`CustomerSearch.tsx`) to a single
 * named file, so this one does not get to add itself. There is also no `GET /jobs/:id` in
 * `jobs.controller.ts` yet. So `job` arrives as a prop, the same way `RxCapture` is handed a
 * customer rather than looking one up, and the caller is responsible for keeping it current.
 */

// ---------------------------------------------------------------------------------------------
// Data shapes. Matching what the job endpoints in `apps/api/src/jobs/jobs.service.ts` read and
// write, not imported from there: `apps/*` never import from each other (`CLAUDE.md`).
// ---------------------------------------------------------------------------------------------

/** The frozen snapshot for one eye. `JOB-001`: "a frozen snapshot ... at the moment of order." */
export interface JobEyeSnapshot {
  readonly sphere: number
  readonly cylinder: number
  readonly axis?: number
  readonly add?: number
  readonly monocularPdMm?: number
  readonly segmentHeightMm?: number
}

/** The most recent quality check on record for this job, if any. */
export interface QualityCheckSummary {
  readonly checkedByStaffId: string
  readonly checkedAt: string
  readonly passed: boolean
  /** Required when it failed. Appendix C.3 assigns a remake's cause from it. */
  readonly failureReason?: string
}

export interface JobDetailData {
  readonly id: string
  /** `TAX-002a`: short and speakable, read out over the phone. */
  readonly jobNumber: string
  readonly status: JobStatus
  /** Set only while `status` is `on_hold`. What `resumeJob` returns to. */
  readonly heldFromStatus?: JobStatus
  /**
   * Appendix C.1's status of the **order**, which is not the job's own. §6.9 `POS-005`.
   *
   * A void unwinds the sale and leaves the job row alone, so a job whose order was cancelled still
   * reads `ready`. Typed `string` rather than an enum for `ARC-020`'s reason, the same as `status`
   * above: a status this build has never heard of must not crash the screen.
   */
  readonly orderStatus: string
  /** The order's currency, for the amount typed into the collect action below. */
  readonly currency: string
  /**
   * **Either eye may be absent, and one eye is a real job.** `single_lens_replacement` carries
   * `requiresEye: true` in `sale-type.ts`, which §12's edge case 114 exists for, so a job with one
   * snapshot is an ordinary sale. Both absent is refused by the mapper, not rendered here.
   */
  readonly od?: JobEyeSnapshot
  readonly os?: JobEyeSnapshot
  readonly latestQualityCheck?: QualityCheckSummary
  /**
   * `RMK-008`: the remakes raised against this job, for the lab credit and the close.
   *
   * Absent means the read did not carry them, which is how an older server reads, so the block is
   * not drawn rather than drawn empty. An empty array means this job has no remakes, which is the
   * ordinary case and also draws nothing.
   */
  readonly remakes?: readonly RemakeLifeRow[]
}

export interface JobDetailProps {
  readonly job: JobDetailData
  /** `CollectJob.branchId`: where the collection is recorded. */
  readonly branchId: string
  /** `CollectJob.terminalId`, `BR-08`: every sale records its terminal. */
  readonly terminalId: string
  /** `CollectJob.cashSessionId`. Required by the server only when cash is actually taken. */
  readonly cashSessionId?: string
  /** Fired after any action here is queued, so the caller knows to refresh `job` when it can. */
  readonly onActionQueued?: () => void
  /**
   * `remake_raise`, Appendix B's row for `POST /jobs/remakes`.
   *
   * Separate from `job_create_advance_status`, which is what the transitions take: `RMK-001` makes
   * raising a remake a decision about who bears a cost, and Appendix B gives it its own row.
   */
  readonly canRaiseRemake?: boolean
  /**
   * The remakes raised against this job, for `RMK-008`'s lab credit and the close. Absent draws
   * nothing, so a caller that has not wired the read yet shows the screen it showed before.
   */
  readonly remakeLife?: RemakeLife
}

/** One remake as the credit and close buttons need it. Dates are the wire's ISO instants. */
export interface RemakeLifeRow {
  readonly id: string
  readonly status: string
  /**
   * Whether the lab owes a credit on this remake. Left undefined when the caller may not see cost
   * figures (`ROLE-001a`): the credit button is then not offered, because pressing it on a remake
   * with no credit expected is a refusal that halts the write queue.
   */
  readonly creditExpected?: boolean
  readonly creditReceivedAt?: string
  readonly resolvedAt?: string
}

export interface RemakeLife {
  readonly rows: readonly RemakeLifeRow[]
  /** `remake_set_cost_attribution`, Appendix B. The server gates all three routes on the same row. */
  readonly canAct: boolean
  /** The branch's IANA zone for `formatDay` (`LOC-004`), never the device's. */
  readonly timezone: string
}

// ---------------------------------------------------------------------------------------------
// Vocabulary. A shopkeeper's words for Appendix C.2's states, not the column names.
// ---------------------------------------------------------------------------------------------

/** The verb for moving forward into a status, not the noun `STATUS_LABEL` uses to name it. */
const ACTION_KEY: Readonly<Record<JobStatus, MessageKey>> = {
  draft: 'action.job.draft',
  ordered: 'action.job.ordered',
  sent: 'action.job.sent',
  chasing: 'action.job.chasing',
  acknowledged: 'action.job.acknowledged',
  in_production: 'action.job.inProduction',
  shipped: 'action.job.shipped',
  received: 'action.job.received',
  qc: 'action.job.qc',
  qc_failed: 'action.job.qcFailed',
  remake_raised: 'action.job.remakeRaised',
  ready: 'action.job.ready',
  collected: 'action.job.collected',
  on_hold: 'action.job.onHold',
  cancelled: 'action.job.cancelled',
}

/**
 * Why nothing more is offered from a state whose only forward edge this screen excludes.
 *
 * `qc_failed`'s one edge besides `on_hold` is `remake_raised`, excluded above; `remake_raised`,
 * `collected` and `cancelled` have no outgoing edges in `JOB_TRANSITIONS` at all. Without this, an
 * empty actions area on those four statuses would look unfinished rather than correct.
 */
function deadEndNote(t: Translation['t'], status: JobStatus): string | null {
  switch (status) {
    case 'qc_failed':
      // The remake is raised here now, so this note is only for a reader without the row: the
      // panel below replaces it for anybody who can act.
      return t('jobDetail.deadEnd.qcFailedNoRow')
    case 'remake_raised':
      return t('jobDetail.deadEnd.remakeRaised')
    case 'collected':
      return t('jobDetail.deadEnd.collected')
    case 'cancelled':
      return t('jobDetail.deadEnd.cancelled')
    default:
      return null
  }
}

// ---------------------------------------------------------------------------------------------
// Which transitions Appendix C.2 permits from here, and only from here.
// ---------------------------------------------------------------------------------------------

const HANDLED_ELSEWHERE = new Set<JobStatus>(['collected', 'remake_raised'])

/** Builds the `QualityCheck` `checkJobTransition` reads, or nothing when none has been recorded. */
function qualityCheckContext(
  jobId: string,
  check: QualityCheckSummary | undefined,
): { readonly qualityCheck?: QualityCheck } {
  if (check === undefined) return {}
  const record: QualityCheck = {
    jobId,
    checkedByStaffId: check.checkedByStaffId,
    checkedAt: new Date(check.checkedAt),
    passed: check.passed,
    ...(check.failureReason === undefined ? {} : { failureReason: check.failureReason }),
    measurements: {},
  }
  return { qualityCheck: record }
}

/**
 * The destinations to offer as generic "move" buttons, in table order.
 *
 * `on_hold` is answered separately: Appendix C.2's "any → on_hold → back" means the return edge is
 * history, not structure, and `JOB_TRANSITIONS['on_hold']` lists every state it is reachable from
 * rather than the one this particular job resumes to. `resumeJob` is the domain's own guard for
 * that, named in `state/job.ts`'s own comment as the fix for exactly this: offering the whole list
 * "would be a path around the QC gate that the table permits and the requirement does not."
 */
function transitionCandidates(job: JobDetailData): readonly JobStatus[] {
  if (job.status === 'on_hold') {
    if (job.heldFromStatus === undefined) return []
    try {
      const back = resumeJob(job.heldFromStatus)
      /**
       * **`resumeJob` says where a hold returns to. It does not say the gate will let it.**
       *
       * `checkJobTransition('on_hold', 'ready', ...)` refuses unconditionally, because `JOB-005a`
       * admits `ready` only from `qc` with a passed check. `JOB_TRANSITIONS['ready']` includes
       * `on_hold`, so a job that is already `ready` can legally be held, and this branch then
       * offered "Resume, back to Ready for collection" as the only action on the screen.
       *
       * Pressing it is worse than a button that does nothing. The server answers 409, which
       * `transport.ts` classifies as refused, so `queue.ts` marks the write `blocked`, and
       * `#nextSendable` returns nothing on a blocked head **by design**, so that a payment cannot
       * overtake the sale it belongs to. One press therefore stops every later write in the shop
       * until somebody discards it by hand.
       *
       * So the same filter the other branch applies is applied here. A hold with no legal way back
       * offers nothing, which is honest: the way out is a quality check, not this screen.
       */
      const context = qualityCheckContext(job.id, job.latestQualityCheck)
      return checkJobTransition('on_hold', back, context).allowed ? [back] : []
    } catch {
      return []
    }
  }

  const edges = JOB_TRANSITIONS[job.status] ?? []
  const context = qualityCheckContext(job.id, job.latestQualityCheck)
  return edges.filter(
    (to) => !HANDLED_ELSEWHERE.has(to) && checkJobTransition(job.status, to, context).allowed,
  )
}

function actionVariant(job: JobDetailData, to: JobStatus): 'primary' | 'secondary' | 'danger' {
  if (job.status === 'on_hold') return 'primary'
  if (to === 'cancelled') return 'danger'
  if (to === 'ready') return 'primary'
  return 'secondary'
}

function actionLabel(t: Translation['t'], job: JobDetailData, to: JobStatus): string {
  // `JOB-003`: coming off hold goes back to where the job was held from, so the button names that
  // state rather than the edge. A whole sentence with a `{status}` hole, never two `t()` calls
  // glued together (Appendix S).
  return job.status === 'on_hold'
    ? t('action.job.resumeTo', { status: jobStatusLabel(t, to) })
    : t(ACTION_KEY[to])
}

// ---------------------------------------------------------------------------------------------
// Numbers. Dioptres through the domain's own formatter; money as bigint minor units, never a float.
// ---------------------------------------------------------------------------------------------

/** `RX-03`: the sign is always rendered. Falls back to the raw value rather than throwing on data
 * that crossed the network and turned out not to be a clean 0.25 step. */
function formatPower(value: number): string {
  try {
    return formatDioptre(dioptre(value))
  } catch {
    return String(value)
  }
}

function formatOptionalPower(t: Translation['t'], value: number | undefined): string {
  return value === undefined ? t('common.notRecorded') : formatPower(value)
}

/**
 * Rupees and paisa as typed at the counter, to minor units, without a float anywhere near it.
 * `CLAUDE.md`: "Money is bigint minor units ... never float, never bare decimal." Returns null on
 * anything that is not a plain amount, so the caller can refuse the submission rather than send a
 * guess.
 */
function minorUnitsFromRupees(raw: string): string | null {
  const trimmed = raw.trim()
  if (trimmed === '') return '0'
  const match = /^(\d+)(?:\.(\d{1,2}))?$/.exec(trimmed)
  if (!match) return null
  const whole = match[1] ?? '0'
  const fraction = (match[2] ?? '').padEnd(2, '0')
  return (BigInt(whole) * 100n + BigInt(fraction)).toString()
}

/** The inverse of the above, for the live preview under the field. No helper existed to reuse. */
function formatMinor(minorUnits: string, currency: string): string {
  let value: bigint
  try {
    value = BigInt(minorUnits)
  } catch {
    return minorUnits
  }
  const negative = value < 0n
  const abs = negative ? -value : value
  const whole = abs / 100n
  const fraction = (abs % 100n).toString().padStart(2, '0')
  return `${negative ? '-' : ''}${currency} ${whole.toString()}.${fraction}`
}

function parsedNumber(raw: string): number | undefined {
  const trimmed = raw.trim()
  if (trimmed === '') return undefined
  const n = Number(trimmed)
  return Number.isFinite(n) ? n : undefined
}

// ---------------------------------------------------------------------------------------------
// What the shop is told about the last action this screen queued. `OFF-005`.
// ---------------------------------------------------------------------------------------------

interface LastWrite {
  readonly id: string
  readonly label: string
}

/**
 * One sentence about the connection and pending count, one about the specific write, never a claim
 * that something saved before the server has actually accepted it.
 *
 * **The connection half is shared and the write half is not.** `queue/status-line.ts` holds the
 * copy the other four screens use; this screen labels its write with what the action was ("Quality
 * check: saved."), because one job detail queues a check, a remake and a status change and a
 * sentence naming none of them would not say which one is in the air.
 */
function statusLine(t: Translate, snapshot: QueueSnapshot, lastWrite: LastWrite | null): string {
  const queueState = connectionLine(t, snapshot)
  if (!lastWrite) return queueState

  const label = lastWrite.label
  const write = snapshot.writes.find((w) => w.id === lastWrite.id)
  const writeState =
    write === undefined || write.status === 'sent'
      ? t('jobDetail.write.saved', { label })
      : write.status === 'blocked'
        ? t('jobDetail.write.blocked', {
            label,
            reason: write.error?.message ?? write.error?.code ?? t('queue.write.refusedNoReason'),
          })
        : t('jobDetail.write.pending', { label })
  return t('queue.statusLine', { queueState, writeState })
}

// ---------------------------------------------------------------------------------------------
// The screen.
// ---------------------------------------------------------------------------------------------

export function JobDetail({
  job,
  branchId,
  terminalId,
  cashSessionId,
  onActionQueued,
  canRaiseRemake,
  remakeLife,
}: JobDetailProps): JSX.Element {
  const { enqueue, snapshot } = useWriteQueue()
  const { t, locale } = useTranslation()
  /**
   * **Whether the sale behind this job is still live**, asked with the function the write path
   * uses. §6.9 `POS-005`; Appendix C.1.
   *
   * A void unwinds the sale and leaves the job row untouched, so a job whose order was cancelled
   * still reads `ready`. This form was gated on that alone, which meant the screen offered to hand
   * the glasses over and `jobs.collect` refused the write with `ORDER_NOT_COLLECTABLE`. A refusal
   * is final to the write queue and a blocked write holds every write behind it, so one press
   * stopped that till until somebody discarded it, and the dispenser found out after the glasses
   * were off the shelf and in front of the customer.
   *
   * `checkOrderTransition` rather than a `collectable` boolean from the server, and rather than a
   * rule written again here: `jobs.service.ts` asks exactly this, and a second implementation is
   * the divergence `job-detail-read.service.ts` names in its own docstring. `fulfilled` is allowed
   * on its own because a second collection on an already-fulfilled order is the remake path, which
   * that service handles separately.
   */
  const collectable =
    job.orderStatus === 'fulfilled' ||
    // Cast at the boundary, the same as `status` above and for `ARC-020`'s reason: the field is
    // `string` so a status this build has never heard of reaches the screen instead of crashing
    // the mapper. `checkOrderTransition` answers `allowed: false` for anything it does not know,
    // which is the safe direction: an unknown status does not open the collect form.
    checkOrderTransition(job.orderStatus as OrderStatus, 'fulfilled').allowed
  const [lastWrite, setLastWrite] = useState<LastWrite | null>(null)
  /** The cause a remake is raised under. `RMK-002`'s fixed set; no default, because it decides who pays. */
  const [remakeCause, setRemakeCause] = useState('')
  const [remakeError, setRemakeError] = useState<string | null>(null)
  const [remakeWriteId, setRemakeWriteId] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  // Quality check form state. Kept as one map rather than eight `useState` calls: the fields are
  // read only at submit time and never individually derived from.
  const [measured, setMeasured] = useState<Record<string, string>>({})
  const [passed, setPassed] = useState<boolean | null>(null)
  const [failureReason, setFailureReason] = useState('')
  const [frameCondition, setFrameCondition] = useState('')
  const [qcError, setQcError] = useState<string | null>(null)

  const [cashInput, setCashInput] = useState('')

  /**
   * The re-read runs when the transition has actually been made, not when it was queued.
   *
   * `onActionQueued` re-reads the job, because `JOB-005a`'s gate and the permitted transitions are
   * the server's answer and a screen that guessed the next state would offer a step the write path
   * then refuses. Fired at enqueue time it read the job back before the POST had been sent, so the
   * screen announced "Mark ordered from lab: saved" beside a status still reading Draft, and
   * offered the same button again.
   */
  useWriteSettled(snapshot, lastWrite?.id ?? null, () => {
    onActionQueued?.()
  })

  const runAction = async (label: string, path: string, body: Record<string, unknown>) => {
    setBusy(true)
    try {
      // `enqueue`'s return type is `unknown` at the hook boundary (`use-write-queue.tsx`); `id` is
      // the one field every write carries and `RxCapture` narrows it the same way.
      const write = (await enqueue({ method: 'POST', path, label, body })) as { id: string }
      setLastWrite({ id: write.id, label })
    } finally {
      setBusy(false)
    }
  }

  const transition = (to: JobStatus): void => {
    const label = t('jobDetail.writeLabel', { action: t(ACTION_KEY[to]), number: job.jobNumber })
    void runAction(label, '/jobs/transition', {
      jobId: job.id,
      to,
    })
  }

  const submitQualityCheck = (event: React.FormEvent): void => {
    event.preventDefault()
    if (passed === null) {
      setQcError(t('jobDetail.qc.errorNoResult'))
      return
    }
    if (!passed && failureReason.trim() === '') {
      // Appendix C.3 assigns a remake's cause from this. A failure with no reason cannot become one.
      setQcError(t('jobDetail.qc.errorNoReason'))
      return
    }
    setQcError(null)

    const eye = (side: 'od' | 'os'): Record<string, number> => {
      const out: Record<string, number> = {}
      const sphere = parsedNumber(measured[`${side}.sphere`] ?? '')
      const cylinder = parsedNumber(measured[`${side}.cylinder`] ?? '')
      const axis = parsedNumber(measured[`${side}.axis`] ?? '')
      const add = parsedNumber(measured[`${side}.add`] ?? '')
      if (sphere !== undefined) out['sphere'] = sphere
      if (cylinder !== undefined) out['cylinder'] = cylinder
      if (axis !== undefined) out['axis'] = axis
      if (add !== undefined) out['add'] = add
      return out
    }
    const pd = parsedNumber(measured['pd'] ?? '')
    const segmentHeight = parsedNumber(measured['segmentHeight'] ?? '')

    void runAction(
      t('jobDetail.qc.writeLabel', { jobNumber: job.jobNumber }),
      '/jobs/quality-check',
      {
        jobId: job.id,
        passed,
        ...(passed ? {} : { failureReason: failureReason.trim() }),
        measurements: {
          od: eye('od'),
          os: eye('os'),
          ...(pd === undefined ? {} : { monocularPdMm: pd }),
          ...(segmentHeight === undefined ? {} : { segmentHeightMm: segmentHeight }),
        },
        ...(frameCondition.trim() === '' ? {} : { frameCondition: frameCondition.trim() }),
      },
    )
  }

  const submitCollect = (event: React.FormEvent): void => {
    event.preventDefault()
    const minor = minorUnitsFromRupees(cashInput)
    if (minor === null) {
      // The submit button is disabled whenever `cashParsed` (the same parse, read at render) is
      // null, so this only guards a submit that reaches the handler some other way, e.g. Enter in
      // the field. Nothing here belongs to the quality-check form's error state.
      return
    }
    void runAction(
      t('jobDetail.collect.writeLabel', { jobNumber: job.jobNumber }),
      '/jobs/collect',
      {
        jobId: job.id,
        branchId,
        terminalId,
        ...(cashSessionId === undefined ? {} : { cashSessionId }),
        ...(minor === '0' ? {} : { cashReceivedMinor: minor }),
      },
    )
  }

  const candidates = transitionCandidates(job)
  const raiseRemake = useCallback(async () => {
    if (remakeCause === '') {
      // `RMK-002` names no default cause and the cause decides the expected bearer of cost, so
      // the screen asks rather than choosing one. The server refuses an absent one besides.
      setRemakeError(t('jobDetail.remake.causeRequired'))
      return
    }
    setRemakeError(null)
    const write = (await enqueue({
      method: 'POST',
      path: '/jobs/remakes',
      label: t('jobDetail.remake.writeLabel', { jobNumber: job.jobNumber }),
      // No `tenantCauseCode`: `RMK-002` makes the sub-cause tenant-extensible and nothing stores a
      // tenant's own list, so there is nothing to offer. No `policyVersionId` either: `RMK-010`
      // makes the policy in force at order time the one that governs and no read carries it.
      body: { originalJobId: job.id, rootCause: remakeCause },
    })) as { id: string }
    setRemakeWriteId(write.id)
    onActionQueued?.()
  }, [enqueue, job.id, job.jobNumber, onActionQueued, remakeCause, t])

  /** `RMK-008`: start, credit and close are three small writes on the remake, none of them a form. */
  const remakeStep = (
    path: '/jobs/remakes/start' | '/jobs/remakes/credit-received' | '/jobs/remakes/resolve',
    labelKey:
      | 'jobDetail.remakeLife.startWriteLabel'
      | 'jobDetail.remakeLife.creditWriteLabel'
      | 'jobDetail.remakeLife.resolveWriteLabel',
    remakeId: string,
  ): void => {
    void runAction(t(labelKey, { jobNumber: job.jobNumber }), path, { remakeId })
  }

  const note = deadEndNote(t, job.status)
  const cashParsed = minorUnitsFromRupees(cashInput)

  return (
    <section className="lm-screen" aria-labelledby="job-detail-heading">
      <h1 id="job-detail-heading" className="lm-screen__heading">
        {sentence(t, 'jobDetail.heading', { jobNumber: <Ltr>{job.jobNumber}</Ltr> })}
      </h1>

      <p>
        {t('jobDetail.statusPrefix')}{' '}
        <span className="lm-badge">{jobStatusLabel(t, job.status)}</span>
      </p>

      <div>
        <h2>{t('jobDetail.rx.heading')}</h2>
        <table>
          <caption className="lm-visually-hidden">
            {t('jobDetail.rx.tableCaption', { jobNumber: job.jobNumber })}
          </caption>
          <thead>
            <tr>
              <th scope="col">{t('rx.col.eye')}</th>
              <th scope="col">{t('rx.col.sphere')}</th>
              <th scope="col">{t('rx.col.cylinder')}</th>
              <th scope="col">{t('rx.col.axis')}</th>
              <th scope="col">{t('rx.col.add')}</th>
              <th scope="col">{t('jobDetail.rx.col.pd')}</th>
              <th scope="col">{t('jobDetail.rx.col.segmentHeight')}</th>
            </tr>
          </thead>
          <tbody>
            {(['od', 'os'] as const).map((side) => {
              const eye = job[side]
              // A single lens replacement has one eye. Rendering "not recorded" across the missing
              // row would read as a gap in the record rather than as the shape of the sale.
              if (eye === undefined) return null
              return (
                <tr key={side}>
                  <th scope="row">
                    {side === 'od' ? t('jobDetail.rx.row.right') : t('jobDetail.rx.row.left')}
                  </th>
                  <td dir="ltr">{formatPower(eye.sphere)}</td>
                  <td dir="ltr">{formatPower(eye.cylinder)}</td>
                  <td dir="ltr">{eye.axis === undefined ? t('common.notRecorded') : eye.axis}</td>
                  <td dir="ltr">{formatOptionalPower(t, eye.add)}</td>
                  <td dir="ltr">
                    {eye.monocularPdMm === undefined ? t('common.notRecorded') : eye.monocularPdMm}
                  </td>
                  <td dir="ltr">
                    {eye.segmentHeightMm === undefined
                      ? t('common.notRecorded')
                      : eye.segmentHeightMm}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
        <p className="lm-hint">{t('jobDetail.rx.frozenNote')}</p>
      </div>

      {job.latestQualityCheck ? (
        <div>
          <h2>{t('jobDetail.qcLatest.heading')}</h2>
          <dl>
            <dt>{t('jobDetail.qc.result')}</dt>
            <dd>
              {job.latestQualityCheck.passed ? t('jobDetail.qc.passed') : t('jobDetail.qc.failed')}
            </dd>
            {job.latestQualityCheck.failureReason !== undefined ? (
              <>
                <dt>{t('common.reason')}</dt>
                <dd>{job.latestQualityCheck.failureReason}</dd>
              </>
            ) : null}
          </dl>
        </div>
      ) : null}

      {job.status === 'qc' ? (
        <form className="lm-form" onSubmit={submitQualityCheck} aria-labelledby="qc-heading">
          <h2 id="qc-heading">{t('jobDetail.qc.heading')}</h2>
          {/* JOB-005a: "Record verified lens powers (measured vs ordered), axis, add, PD, segment
              height, frame condition." */}
          <p className="lm-hint">{t('jobDetail.qc.measuredAgainst')}</p>

          {(['od', 'os'] as const).map((side) => {
            const eye = job[side]
            // `JOB-005a` verifies measured against ordered, and there is nothing ordered for an eye
            // this job was never made for. A single lens replacement is checked on one eye.
            if (eye === undefined) return null
            return (
              <table key={side}>
                <caption>
                  {side === 'od'
                    ? t('jobDetail.qc.eyeCaption.right')
                    : t('jobDetail.qc.eyeCaption.left')}
                </caption>
                <thead>
                  <tr>
                    <th scope="col">{t('jobDetail.qc.col.field')}</th>
                    <th scope="col">{t('jobDetail.qc.col.ordered')}</th>
                    <th scope="col">{t('jobDetail.qc.col.measured')}</th>
                  </tr>
                </thead>
                <tbody>
                  {/* The field's key is written out rather than built from `field`, because
                      `i18n-static.test.ts` reads the catalogue's keys out of the source: a key
                      assembled in a template literal is a key the gate cannot see, and it reported
                      all four of these as words nothing uses. */}
                  {(
                    [
                      [
                        'sphere',
                        'jobDetail.qc.field.sphere',
                        t('rx.col.sphere'),
                        formatPower(eye.sphere),
                      ],
                      [
                        'cylinder',
                        'jobDetail.qc.field.cylinder',
                        t('rx.col.cylinder'),
                        formatPower(eye.cylinder),
                      ],
                      [
                        'axis',
                        'jobDetail.qc.field.axis',
                        t('rx.col.axis'),
                        eye.axis === undefined ? t('common.notRecorded') : String(eye.axis),
                      ],
                      [
                        'add',
                        'jobDetail.qc.field.add',
                        t('rx.col.add'),
                        formatOptionalPower(t, eye.add),
                      ],
                    ] as const
                  ).map(([field, fieldKey, label, ordered]) => (
                    <tr key={field}>
                      <th scope="row">{label}</th>
                      <td dir="ltr">{ordered}</td>
                      <td>
                        <label>
                          <span className="lm-visually-hidden">
                            {t('jobDetail.qc.measuredEye', {
                              side:
                                side === 'od'
                                  ? t('jobDetail.qc.side.right')
                                  : t('jobDetail.qc.side.left'),
                              field: t(fieldKey),
                            })}
                          </span>
                          <input
                            className="lm-input"
                            type="text"
                            inputMode={field === 'axis' ? 'numeric' : 'decimal'}
                            autoComplete="off"
                            value={measured[`${side}.${field}`] ?? ''}
                            onChange={(e) =>
                              setMeasured((m) => ({
                                ...m,
                                [`${side}.${field}`]: e.currentTarget.value,
                              }))
                            }
                          />
                        </label>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )
          })}

          <label className="lm-field lm-field--inline">
            <span className="lm-field__label">{t('jobDetail.qc.monocularPd')}</span>
            <input
              className="lm-input"
              type="text"
              inputMode="decimal"
              autoComplete="off"
              value={measured['pd'] ?? ''}
              onChange={(e) => setMeasured((m) => ({ ...m, pd: e.currentTarget.value }))}
            />
          </label>

          <label className="lm-field lm-field--inline">
            <span className="lm-field__label">{t('jobDetail.rx.col.segmentHeight')}</span>
            <input
              className="lm-input"
              type="text"
              inputMode="decimal"
              autoComplete="off"
              value={measured['segmentHeight'] ?? ''}
              onChange={(e) => setMeasured((m) => ({ ...m, segmentHeight: e.currentTarget.value }))}
            />
          </label>

          <label className="lm-field">
            <span className="lm-field__label">{t('jobDetail.qc.frameCondition')}</span>
            <input
              className="lm-input"
              type="text"
              value={frameCondition}
              onChange={(e) => setFrameCondition(e.currentTarget.value)}
            />
          </label>

          <fieldset>
            <legend>{t('jobDetail.qc.result')}</legend>
            <label className="lm-field--inline">
              <input
                type="radio"
                name={`qc-result-${job.id}`}
                checked={passed === true}
                onChange={() => setPassed(true)}
              />
              <span>{t('jobDetail.qc.passed')}</span>
            </label>
            <label className="lm-field--inline">
              <input
                type="radio"
                name={`qc-result-${job.id}`}
                checked={passed === false}
                onChange={() => setPassed(false)}
              />
              <span>{t('jobDetail.qc.failed')}</span>
            </label>
          </fieldset>

          {passed === false ? (
            <label className="lm-field">
              <span className="lm-field__label">{t('jobDetail.qc.reasonFailed')}</span>
              <input
                className="lm-input"
                type="text"
                value={failureReason}
                onChange={(e) => setFailureReason(e.currentTarget.value)}
              />
            </label>
          ) : null}

          {qcError ? (
            <p className="lm-hint" role="status">
              {qcError}
            </p>
          ) : null}

          <div className="lm-screen__actions">
            <button type="submit" className="lm-button lm-button--primary" disabled={busy}>
              {t('jobDetail.qc.save')}
            </button>
          </div>
        </form>
      ) : null}

      <div>
        <h2>{t('jobDetail.actions.heading')}</h2>
        {note ? <p className="lm-hint">{note}</p> : null}

        {job.status === 'qc_failed' && (canRaiseRemake ?? false) ? (
          <section className="lm-remake" aria-labelledby="remake-heading">
            <h3 id="remake-heading">{t('jobDetail.remake.heading')}</h3>
            {/* What raising one does, before it is done. `RMK-001` makes the new job carry the
                original's frozen snapshot and `RMK-004` leaves the original closed, so this is not
                a reopening: it is a second pair made to the same instruction. */}
            <p className="lm-hint">{t('jobDetail.remake.means')}</p>
            <label className="lm-field lm-field--inline">
              <span className="lm-field__label">{t('jobDetail.remake.causeLabel')}</span>
              <select
                className="lm-input"
                value={remakeCause}
                onChange={(e) => {
                  setRemakeCause(e.currentTarget.value)
                }}
              >
                <option value="">{t('jobDetail.remake.causePick')}</option>
                {REMAKE_CAUSES.map((cause) => (
                  <option key={cause} value={cause}>
                    {remakeCauseLabel(t, cause)}
                  </option>
                ))}
              </select>
            </label>
            {/* `RMK-002` assigns the typical bearer of cost from the cause, and `RMK-003` lets a
                human change it afterwards. Said here because choosing a cause is choosing who is
                expected to pay, which is not obvious from a list of ten words. */}
            <p className="lm-hint">{t('jobDetail.remake.causeMeans')}</p>
            <button
              type="button"
              className="lm-button lm-button--primary"
              disabled={remakeWriteId !== null}
              onClick={() => {
                void raiseRemake()
              }}
            >
              {t('jobDetail.remake.raise')}
            </button>
            {remakeError === null ? null : (
              <p className="lm-error" role="alert">
                {remakeError}
              </p>
            )}
          </section>
        ) : null}
        {remakeLife !== undefined && remakeLife.rows.length > 0 ? (
          <section className="lm-remake-life" aria-labelledby="remake-life-heading">
            <h3 id="remake-life-heading">{t('jobDetail.remakeLife.heading')}</h3>
            <ul>
              {remakeLife.rows.map((row) => {
                const canStart = remakeLife.canAct && row.status === 'new_job_created'
                const canCredit =
                  remakeLife.canAct &&
                  row.creditExpected === true &&
                  row.creditReceivedAt === undefined &&
                  [
                    'costed',
                    'credit_expected',
                    'new_job_created',
                    'in_progress',
                    'resolved',
                  ].includes(row.status)
                const canResolve =
                  remakeLife.canAct &&
                  (row.status === 'in_progress' || row.status === 'credit_received')
                return (
                  <li key={row.id} data-remake-id={row.id}>
                    <span>{remakeStatusWord(t, row.status)}</span>
                    {row.creditReceivedAt === undefined ? null : (
                      <p className="lm-hint">
                        {t('jobDetail.remakeLife.creditAt')}{' '}
                        <Ltr>{formatDay(row.creditReceivedAt, remakeLife.timezone, locale)}</Ltr>
                      </p>
                    )}
                    {row.resolvedAt === undefined ? null : (
                      <p className="lm-hint">
                        {t('jobDetail.remakeLife.resolvedAt')}{' '}
                        <Ltr>{formatDay(row.resolvedAt, remakeLife.timezone, locale)}</Ltr>
                      </p>
                    )}
                    <div className="lm-screen__actions">
                      {canStart ? (
                        <button
                          type="button"
                          className="lm-button lm-button--secondary"
                          disabled={busy}
                          onClick={() => {
                            remakeStep(
                              '/jobs/remakes/start',
                              'jobDetail.remakeLife.startWriteLabel',
                              row.id,
                            )
                          }}
                        >
                          {t('jobDetail.remakeLife.start')}
                        </button>
                      ) : null}
                      {canCredit ? (
                        <button
                          type="button"
                          className="lm-button lm-button--secondary"
                          disabled={busy}
                          onClick={() => {
                            remakeStep(
                              '/jobs/remakes/credit-received',
                              'jobDetail.remakeLife.creditWriteLabel',
                              row.id,
                            )
                          }}
                        >
                          {t('jobDetail.remakeLife.credit')}
                        </button>
                      ) : null}
                      {canResolve ? (
                        <button
                          type="button"
                          className="lm-button lm-button--primary"
                          disabled={busy}
                          onClick={() => {
                            remakeStep(
                              '/jobs/remakes/resolve',
                              'jobDetail.remakeLife.resolveWriteLabel',
                              row.id,
                            )
                          }}
                        >
                          {t('jobDetail.remakeLife.resolve')}
                        </button>
                      ) : null}
                    </div>
                  </li>
                )
              })}
            </ul>
          </section>
        ) : null}
        {job.status === 'on_hold' && candidates.length === 0 ? (
          <p className="lm-hint">{t('jobDetail.onHoldNoResume')}</p>
        ) : null}
        {candidates.length > 0 ? (
          <div className="lm-screen__actions">
            {candidates.map((to) => (
              <button
                key={to}
                type="button"
                className={`lm-button lm-button--${actionVariant(job, to)}`}
                disabled={busy}
                onClick={() => transition(to)}
              >
                {actionLabel(t, job, to)}
              </button>
            ))}
          </div>
        ) : null}
      </div>

      {job.status === 'ready' && !collectable ? (
        // Not a blank space where a form was: a dispenser holding a finished pair needs to be told
        // why they cannot hand it over, and the sale is what changed rather than the job.
        <p className="lm-hint">{t('jobDetail.collect.orderNotCollectable')}</p>
      ) : null}

      {job.status === 'ready' && collectable ? (
        <form className="lm-form" onSubmit={submitCollect} aria-labelledby="collect-heading">
          <h2 id="collect-heading">{t('jobDetail.collect.heading')}</h2>
          <label className="lm-field">
            <span className="lm-field__label">{t('jobDetail.collect.cashLabel')}</span>
            <input
              className="lm-input"
              type="text"
              inputMode="decimal"
              autoComplete="off"
              value={cashInput}
              onChange={(e) => setCashInput(e.currentTarget.value)}
            />
          </label>
          <p className="lm-hint">
            {cashParsed === null
              ? t('jobDetail.collect.invalid')
              : sentence(t, 'jobDetail.collect.preview', {
                  amount: <Ltr>{formatMinor(cashParsed, job.currency)}</Ltr>,
                })}
          </p>
          <div className="lm-screen__actions">
            <button
              type="submit"
              className="lm-button lm-button--primary"
              disabled={busy || cashParsed === null}
            >
              {t('jobDetail.collect.submit')}
            </button>
          </div>
        </form>
      ) : null}

      <p className="lm-queue-state" role="status">
        {statusLine(t, snapshot, lastWrite)}
      </p>
    </section>
  )
}
